import { defineMessages, useIntl } from 'react-intl';
import { Button, Form } from 'react-aria-components';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import {
  useDispatch,
  useSelector,
  type TypedUseSelectorHook,
} from 'react-redux';
import cx from 'classnames';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  resetSubmitContactForm,
  submitContactForm,
} from '@redturtle/qubicaamf-theme/actions';
import type { SubmitContactFormInitialState } from '@redturtle/qubicaamf-theme/reducers';
import config from '@plone/volto/registry';

import styles from '../styles.module.css';
import GeneralInfoSection from './GeneralInfoSection';
import PrivacySection from './PrivacySection';
import ThankYouPage from './thankYouPage';
import ErrorPage from './ErrorPage';
import {
  newInvestmentBusinessInfo,
  newInvestmentFormFieldsInfo,
  newInvestmentFormLocationInfo,
} from './formFields';

export interface FormProps {
  nations?: {
    title: string;
    token: string;
  }[];
}

interface RootState {
  submitContactForm: SubmitContactFormInitialState;
}

const NewInvestmentForm = (props: FormProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const intl = useIntl();
  const googleRecaptchaKey = config.settings.gRecaptchaKey as string;

  const { nations } = props;

  const useTypedSelector: TypedUseSelectorHook<RootState> =
    useSelector<RootState>;

  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let data = Object.fromEntries(new FormData(e.currentTarget));
    dispatch(submitContactForm('@new-investment-inquiries', data));
  };

  useEffect(() => {
    dispatch(resetSubmitContactForm());
  }, [location.pathname, dispatch]);

  const submitState = useTypedSelector((state) => state.submitContactForm);

  const submitError = submitState.error;
  const submitSuccess = !submitState.error && submitState.loaded;

  return (
    <div className={styles.formWrapper}>
      <GoogleReCaptchaProvider reCaptchaKey={googleRecaptchaKey}>
        {!!!submitError && !submitState.loading && !submitSuccess && (
          <Form
            className={cx(styles.form, styles.newInvestmentForm)}
            onSubmit={(e) => submitForm(e)}
          >
            <h2 className={styles.personalInfoTitle}>
              {intl.formatMessage(messages.personal_info_section_title)}
            </h2>
            <GeneralInfoSection
              nations={nations}
              fieldsInfo={newInvestmentFormFieldsInfo}
            />

            <div>
              <h2>{intl.formatMessage(messages.project_info_section_title)}</h2>
              <div>
                {intl.formatMessage(messages.project_info_section_text)}
              </div>
            </div>

            <div className={styles.radioInfoSection}>
              <h2>
                {intl.formatMessage(messages.location_info_section_title)}
              </h2>
              <GeneralInfoSection fieldsInfo={newInvestmentFormLocationInfo} />
            </div>

            <div className={styles.radioInfoSection}>
              <h2>
                {intl.formatMessage(messages.business_info_section_title)}
              </h2>
              <GeneralInfoSection fieldsInfo={newInvestmentBusinessInfo} />
            </div>

            <PrivacySection />
            <Button type="submit" className={styles.submitButton}>
              {intl.formatMessage(messages.submit_button)}
            </Button>
          </Form>
        )}
        {!!submitError && <ErrorPage />}
        {!!!submitError && submitSuccess && (
          <ThankYouPage form_type="new_inquiries" />
        )}
      </GoogleReCaptchaProvider>
    </div>
  );
};

export default NewInvestmentForm;

const messages = defineMessages({
  personal_info_section_title: {
    id: 'personal_info_section_title',
    defaultMessage: 'Personal Information',
  },
  project_info_section_title: {
    id: 'project_info_section_title',
    defaultMessage: 'Project Information',
  },
  project_info_section_text: {
    id: 'project_info_section_text',
    defaultMessage:
      'New bowling projects are large capital investments and require significant financing. New bowling equipment packages start at $35,000 per lane including installation. Typically, lenders require 20% equity to obtain financing for your investment.',
  },
  location_info_section_title: {
    id: 'location_info_section_title',
    defaultMessage: 'Location Information',
  },
  business_info_section_title: {
    id: 'business_info_section_title',
    defaultMessage: 'Business Information',
  },
  submit_button: {
    id: 'submit_button',
    defaultMessage: 'Submit',
  },
});
